<script setup>
import ProductFormDialog from '@/components/products-page/ProductFormDialog.vue'
import BatchPriceUpdateDialog from '@/components/products-page/BatchPriceUpdateDialog.vue'
import BaseAlert from '@/components/base/BaseAlert.vue'
import ProductsFilter from '@/components/products-page/ProductsFilter.vue'
import BaseDeleteConfirm from '@/components/base/BaseDeleteConfirm.vue'
import BaseStatusChip from '@/components/base/BaseStatusChip.vue'
import ProductPricingTable from '@/components/products-page/ProductPricingTable.vue'
import IconDollar from '@/components/iconDollar.vue'
import BasePagination from '@/components/base/BasePagination.vue'
// import ProductLatestPrice from '@/components/products-page/ProductLatestPrice.vue'

import { useStore } from '@/utils/VuexHelpers'
import { computed, onUnmounted, ref } from 'vue'
import { PRODUCTS } from '@/constants/pagination'
import { formatCurrency } from '@/helpers/stringHelpers.js'

const batchPriceDialogRef = ref(null)

const store = useStore()
const productList = computed(() => store.getters.products)
const sortbyProd = computed(() => store.getters.sortbyProd)
const loadingProducts = computed(() => store.getters.loadingProducts)
const ascProd = computed(() => !store.getters.ascProd)
const perPageProducts = computed(() => store.getters.perPageProducts)
const totalProducts = computed(() => store.getters.totalProducts)
const pageNumberProd = computed(() => store.getters.pageNumberProd)
const perPageOptions = computed(() => store.getters.perPageOptions)

const headers = [
  {
    value: 'product',
    text: 'Name',
    sortable: true,
    cellClass: 'product-name-cell'
  },
  { value: 'code', text: 'Code', sortable: true },
  { value: 'type', text: 'Type', sortable: true },
  { value: 'pack', text: 'Pack', sortable: true },
  { value: 'soaker', text: 'Soaker Insert', sortable: false },
  { value: 'PCS', text: 'PCS', sortable: false },
  { value: 'cnt_base', text: 'Cnt Base', sortable: false },
  { value: 'ref_status', text: 'Ref Status', sortable: false },
  {
    value: 'est_yield',
    text: 'Est Yield',
    cellClass: 'text-end',
    class: 'text-end',
    sortable: false
  },
  {
    value: 'default_price',
    text: 'Price/Kg',
    cellClass: 'text-end',
    class: 'text-end',
    sortable: true
  },
  { value: 'comment', text: 'Comment', sortable: false },
  { value: 'data-table-expand', text: 'Actions', sortable: false }
]

const selectedProducts = ref([])
const isBatchPriceDialogOpen = ref(false)
const expanded = ref([])

const perPage = computed({
  get: () => perPageProducts.value,
  set: (value) => {
    store.commit('SET_PER_PAGE_PROD', value)
    store.dispatch('fetchProducts')
  }
})

const deleteProd = async (info) => {
  let res = await store.dispatch('deleteProduct', { payload: info })
  if (res === 'success') {
    store.dispatch('fetchProducts')
  }
}

const sortChange = ({ sortBy, sortDesc }) => {
  store.commit('SET_ASC_PROD', !sortDesc[0])
  store.commit('SET_SORT_BY_PROD', sortBy[0])
  store.dispatch('fetchProducts')
}
const updatePageNr = (nr) => {
  store.commit('SET_PAGE_NR_PROD', nr - 1)
  store.dispatch('fetchProducts')
}

const handleBatchPriceSave = async (newPrices) => {
  const { effectiveDate, endDate, comment, items, customerIds } = newPrices

  const payload = customerIds?.length
    ? customerIds.flatMap((customerId) => {
        return items.map((price) => ({
          productlist_id: price.productId,
          customer_id: customerId,
          start_date: effectiveDate,
          end_date: !endDate || endDate === '' ? null : endDate,
          price_kg: price.newPriceKg,
          comment
        }))
      })
    : items.map((price) => ({
        productlist_id: price.productId,
        start_date: effectiveDate,
        price_kg: price.newPriceKg,
        end_date: !endDate || endDate === '' ? null : endDate,
        comment
      }))

  const res = await store.dispatch('batchCreateProdPrice', { payload })
  if (res !== 'error') {
    isBatchPriceDialogOpen.value = false
    selectedProducts.value = []
    batchPriceDialogRef.value?.resetForm()
    store.dispatch('fetchProducts')
  }
}

// Clear cached products on leave so the table doesn't render cached rows immediately
// and slow down navigation back to this page.
onUnmounted(() => {
  store.commit('SET_PRODUCT_LIST', [])
})

// ON CREATED
store.commit('SET_USING_VUETIFY', true)
store.commit('SET_ACTIVE', PRODUCTS)
</script>

<template>
  <v-app>
    <BaseAlert />
    <v-container fluid>
      <div class="wrapper">
        <ProductsFilter />
        <div class="content-page mt-0">
          <v-row no-gutters class="my-2 align-center">
            <div class="page-title-box d-flex">
              <h4 class="page-title">Products</h4>
            </div>
          </v-row>
          <v-row class="mt-1 mb-4 align-center" no-gutters>
            <ProductFormDialog />
            <div class="d-flex align-center">
              Total Products:
              <span class="font-weight-bold ml-2">{{ totalProducts }}</span>
            </div>
            <div class="ms-auto mr-2 d-flex align-center">
              Products per page:
            </div>
            <b-select
              v-model="perPage"
              :options="perPageOptions"
              class="d-inline-block w-auto mr-4"
            ></b-select>
          </v-row>
          <v-card>
            <v-data-table
              class="elevation-1 pt-2"
              v-model="selectedProducts"
              :headers="headers"
              :items="productList"
              item-key="id"
              :sort-by="sortbyProd"
              :sort-desc="ascProd"
              :show-select="true"
              :height="800"
              fixed-header
              :loading="loadingProducts === 'list'"
              loading-text="Loading... Please wait"
              :server-items-length="perPageProducts"
              hide-default-footer
              @update:options="sortChange"
              show-expand
              :expanded.sync="expanded"
            >
              <template v-slot:top>
                <v-row
                  class="table-top-actions elevation-1 bg-light-blue p-2 justify-start align-center pt-0"
                >
                  <v-col cols="auto"
                    ><small class="text-uppercase"
                      >Selected: {{ selectedProducts.length }}</small
                    ></v-col
                  >
                  <v-col cols="auto">
                    <v-btn
                      x-small
                      color="info"
                      :disabled="selectedProducts.length === 0"
                      @click="isBatchPriceDialogOpen = true"
                    >
                      <span class="d-flex align-center"
                        ><font-awesome-icon
                          :icon="['fas', 'edit']"
                          class="mr-1"
                        />Batch Update Prices</span
                      >
                    </v-btn></v-col
                  >
                </v-row>
                <BatchPriceUpdateDialog
                  ref="batchPriceDialogRef"
                  v-model="isBatchPriceDialogOpen"
                  :selected-products="selectedProducts"
                  @save="handleBatchPriceSave"
                />
              </template>
              <template v-slot:item.code="{ item }">
                <div>{{ item.code }}</div>
                <div v-if="item.barcode" class="text-nowrap">
                  GS1: {{ item.barcode }}
                </div>
              </template>
              <template v-slot:item.default_price="{ value }">
                {{ formatCurrency(value) }}
              </template>
              <template v-slot:item.soaker="{ item }">
                <BaseStatusChip
                  v-if="item.soaker"
                  :label="'Soaker'"
                  color="blue lighten-1"
                />
                <BaseStatusChip
                  v-if="item.insert"
                  :label="'Insert'"
                  color="teal darken-1"
                />
              </template>
              <template v-slot:item.PCS="{ item }">
                <div>Bag: {{ item.pcs_bag }}</div>
                <div>Ctn: {{ item.pcs_ctn }}</div>
              </template>
              <template #item.data-table-expand="{ isExpanded, expand, item }">
                <v-row class="text-nowrap align-center flex-nowrap" no-gutters>
                  <IconDollar
                    @click="() => expand(!isExpanded)"
                    :title="`${isExpanded ? 'Hide' : 'Show'} Pricing History`"
                  />
                  <BaseDeleteConfirm
                    :icon="true"
                    size="small"
                    title="Are you sure you want to delete this product?"
                    @deleteConfirm="deleteProd(item)"
                  >
                    <template v-slot:content>
                      <div class="text-subtitle-2 mt-2">Product Name:</div>
                      <div class="font-weight-bold">{{ item.product }}</div>
                      <div class="text-subtitle-2 mt-2">Product Code:</div>
                      <div class="font-weight-bold">{{ item.code }}</div>
                    </template>
                  </BaseDeleteConfirm>
                  <ProductFormDialog :edit="true" :details="item" />
                </v-row>
              </template>
              <template #expanded-item="{ item }">
                <ProductPricingTable
                  :product="item"
                  :width="headers.length + 1"
                  @refresh="store.dispatch('fetchProducts')"
                />
              </template>
            </v-data-table>
            <div class="ms-4">
              <BasePagination
                :total="totalProducts"
                :per-page="perPageProducts"
                :current-page="pageNumberProd + 1"
                item-label="products"
                :disabled="loadingProducts === 'list'"
                @update:page="updatePageNr"
                :show="true"
              />
            </div>
          </v-card>
        </div>
      </div>
    </v-container>
  </v-app>
</template>

<style>
.product-name-cell {
  min-width: 200px;
}
</style>
